import React, { useEffect, useState, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Telas de entrada ficam eager para não piscar fallback no login
import Login from "./pages/Login/Login";
import SuporteLogin from "./pages/Suporte/login";
import LoginAdmin from "./pages/Admin/Login/login";

// Contextos e infraestrutura compartilhada
import { AdminAuthProvider } from "./contexts/AdminAuthContext";
import { StoreAuthProvider } from "./contexts/StoreAuthContext";
import { SuporteAuthProvider } from "./contexts/SuporteAuthContext";
import { GlobalNotificationsProvider } from "./contexts/GlobalNotificationsContext";
import { SystemMessageProvider } from "./contexts/SystemMessageContext";
import StoreAccessControl from "./components/StoreAccessControl";

import "./App.css";
import "./utils/test429Error"; // Para testes manuais

// ==== Code splitting: páginas carregadas sob demanda ====
const Landing = React.lazy(() => import("./pages/Landing/Landing"));
const Register = React.lazy(() => import("./pages/Register/Register"));
const ForgotPassword = React.lazy(() =>
  import("./pages/ForgotPassword/ForgotPassword")
);
const Dashboard = React.lazy(() => import("./pages/Dashboard/Dashboard"));
const Produtos = React.lazy(() => import("./pages/Produtos/Produtos"));
const Pedidos = React.lazy(() => import("./pages/Pedidos/Pedidos"));
const SuporteDashboard = React.lazy(() => import("./pages/Suporte/dashboard"));
const FinanceiroSuporte = React.lazy(() =>
  import("./pages/Suporte/financeiro")
);
const RegisterSupport = React.lazy(() => import("./pages/Suporte/register"));
const Occurrences = React.lazy(() => import("./pages/Suporte/occurrences"));
const ChatPage = React.lazy(() => import("./pages/Suporte/chat"));
const ConversationsPage = React.lazy(() =>
  import("./pages/Conversations/Conversations")
);
const OcorrenciasPage = React.lazy(() =>
  import("./pages/Ocorrencias/ocorrencias")
);
const ChatStore = React.lazy(() => import("./pages/Chat/chat"));
const SupportMapPage = React.lazy(() => import("./pages/Suporte/map"));
const AdminDashboard = React.lazy(() =>
  import("./pages/Admin/Dashboard/dashboard")
);
const AdminFinanceiro = React.lazy(() =>
  import("./pages/Admin/Financeiro/financeiro")
);
const RelatorioFinanceiro = React.lazy(() =>
  import("./pages/Admin/Relatorios/RelatorioFinanceiro")
);
const RelatorioFinanceiroAgrupado = React.lazy(() =>
  import("./pages/Admin/Relatorios/RelatorioFinanceiroAgrupado")
);
const AdminCorridas = React.lazy(() =>
  import("./pages/Admin/Corridas/Corridas")
);
const OrdersPage = React.lazy(() => import("./pages/Orders/Orders"));
const MotoboysPage = React.lazy(() => import("./pages/Motoboys"));
const EstabelecimentosPage = React.lazy(() =>
  import("./pages/Estabelecimentos")
);
const EstabelecimentosSuporteWrapper = React.lazy(() =>
  import("./pages/Estabelecimentos/EstabelecimentosSupporte")
);
const PrecificacaoPage = React.lazy(() =>
  import("./pages/Precificacao/Precificacao")
);
const SupportPage = React.lazy(() =>
  import("./pages/Admin/SuportTeam/SupportPage")
);
const TiposPage = React.lazy(() => import("./pages/Admin/Tipos/Tipos"));
const MetadadosPage = React.lazy(() =>
  import("./pages/Admin/Metadados/Metadados")
);
const SupportNotifications = React.lazy(() =>
  import("./pages/Suporte/SupportNotifications")
);
const AdminNotifications = React.lazy(() =>
  import("./pages/Admin/Notifications/AdminNotifications")
);
const Termos = React.lazy(() => import("./pages/Termos/Termos"));
const CoordenadasPage = React.lazy(() =>
  import("./pages/Coordenadas/Coordenadas")
);
const CorridasStore = React.lazy(() =>
  import("./pages/Store/Corridas/CorridasStore")
);
const FinanceiroStore = React.lazy(() =>
  import("./pages/Store/Financeiro/financeiro")
);
const StoreNotificationsPage = React.lazy(() =>
  import("./pages/Store/Notifications/StoreNotifications")
);
const Configuracao = React.lazy(() =>
  import("./pages/Store/Configuracao/Configuracao")
);
const Relatorios = React.lazy(() => import("./pages/Relatorios/Relatorios"));
const OcorrenciasReport = React.lazy(() =>
  import("./components/Reports/OcorrenciasReport")
);
const Perfil = React.lazy(() => import("./pages/Store/Perfil/Perfil"));
const MeuRepasse = React.lazy(() =>
  import("./pages/Store/Repasse/Repasse")
);
const PrivacyPolicy = React.lazy(() =>
  import("./components/PrivacyPolicy")
);
const MassNotificationPage = React.lazy(() =>
  import("./pages/MassNotification/page")
);
const PedidosPendentes = React.lazy(() =>
  import("./pages/Store/PedidosPendentes/page")
);
const Fila = React.lazy(() => import("./pages/Admin/Fila/Fila"));
const ContratosPage = React.lazy(() => import("./pages/Contratos"));
const Impressora = React.lazy(() => import("./pages/Impressora/Impressora"));
const PassoAPasso = React.lazy(() =>
  import("./pages/PassoAPasso/PassoAPasso")
);
const Cardapio = React.lazy(() => import("./pages/Cardapio/Cardapio"));
const Marketplace = React.lazy(() => import("./pages/Marketplace/Marketplace"));
const MarketplaceRepasse = React.lazy(() =>
  import("./pages/Admin/Marketplace/Repasse")
);
const PDV = React.lazy(() => import("./pages/Store/PDV/PDV"));
const Funcionarios = React.lazy(() =>
  import("./pages/Store/Funcionarios/Funcionarios")
);

// Fallback exibido enquanto o chunk da página carrega
const PageLoading = () => (
  <Box
    sx={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 2,
    }}
  >
    <CircularProgress />
    <Typography variant="body2" color="text.secondary">
      Carregando...
    </Typography>
  </Box>
);
// Definir tema personalizado com a paleta de cores da Gringo Delivery
const theme = createTheme({
  typography: {
    fontFamily: ["Poppins", "Roboto", "Arial", "sans-serif"].join(","),
    h1: {
      fontFamily: "Poppins, sans-serif",
      fontWeight: 700,
    },
    h2: {
      fontFamily: "Poppins, sans-serif",
      fontWeight: 700,
    },
    h3: {
      fontFamily: "Poppins, sans-serif",
      fontWeight: 600,
    },
    h4: {
      fontFamily: "Poppins, sans-serif",
      fontWeight: 600,
    },
    h5: {
      fontFamily: "Poppins, sans-serif",
      fontWeight: 500,
    },
    h6: {
      fontFamily: "Poppins, sans-serif",
      fontWeight: 500,
    },
    subtitle1: {
      fontFamily: "Poppins, sans-serif",
      fontWeight: 500,
    },
    subtitle2: {
      fontFamily: "Poppins, sans-serif",
      fontWeight: 500,
    },
    body1: {
      fontFamily: "Poppins, sans-serif",
    },
    body2: {
      fontFamily: "Poppins, sans-serif",
    },
    button: {
      fontFamily: "Poppins, sans-serif",
      fontWeight: 500,
      textTransform: "none",
    },
  },
  palette: {
    primary: {
      main: "#EB2E3E", // Vermelho Gringo
      contrastText: "#FFFFFF",
      light: "#f15a68",
      dark: "#d12535",
      lightest: "#fde8ea", // Para backgrounds suaves
    },
    secondary: {
      main: "#FBBF24", // Amarelo Gringo
      contrastText: "#FFFFFF",
      light: "#fcd04b",
      dark: "#e6ad20",
      lightest: "#fef7e6", // Para backgrounds suaves
    },
    background: {
      default: "#f5f5f5",
      paper: "#FFFFFF",
    },
    error: {
      main: "#f44336",
      light: "#f6685e",
      dark: "#d32f2f",
      lightest: "#feeaea", // Para backgrounds suaves
    },
    warning: {
      main: "#FBBF24", // Amarelo Gringo
      light: "#fcd04b",
      dark: "#e6ad20",
      lightest: "#fef7e6", // Para backgrounds suaves
    },
    info: {
      main: "#2196f3",
      light: "#4dabf5",
      dark: "#1976d2",
      lightest: "#e6f4fe", // Para backgrounds suaves
    },
    success: {
      main: "#4caf50",
      contrastText: "#FFFFFF",
      light: "#6fbf73",
      dark: "#3b873e",
      lightest: "#ebf7ec", // Para backgrounds suaves
    },
    text: {
      primary: "#333333",
      secondary: "#666666",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 600,
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: "#d12535", // Vermelho mais escuro
          },
        },
        containedSecondary: {
          "&:hover": {
            backgroundColor: "#e6ad20", // Amarelo mais escuro
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          overflow: "hidden",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#EB2E3E",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: "#EB2E3E",
        },
        colorSecondary: {
          backgroundColor: "#FBBF24",
        },
      },
    },
  },
});

// Componente para rotas protegidas
const PrivateRoute = ({ children }) => {
  const { currentUser } = useAuth();

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function useSubdomain() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuporte, setIsSuporte] = useState(false);

  useEffect(() => {
    const hostname = window.location.hostname;
    const subdomain = hostname.split(".")[0];

    // Verificar se é subdomínio admin
    setIsAdmin(subdomain === "admin");

    setIsSuporte(subdomain === "suporte");
  }, []);

  return { isAdmin, isSuporte };
}

function CustomerApp() {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <SystemMessageProvider>
          <GlobalNotificationsProvider userType="customer">
            <Suspense fallback={<PageLoading />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/privacy-app" element={<PrivacyPolicy />} />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <Dashboard />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/notifications"
                element={<StoreNotificationsPage />}
              />
              <Route path="/passo-a-passo" element={<PassoAPasso />} />
              <Route path="/cardapio" element={<PrivateRoute><Cardapio /></PrivateRoute>} />
              <Route path="/marketplace" element={<PrivateRoute><Marketplace /></PrivateRoute>} />
              <Route
                path="/produtos"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <Produtos />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/pedidos"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <Pedidos />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/pdv"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <PDV />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/funcionarios"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <Funcionarios />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/ocorrencias"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <OcorrenciasPage />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/chat"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <ChatStore />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/corridas"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <CorridasStore />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/financeiro"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <FinanceiroStore />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/meu-repasse"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <MeuRepasse />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/coordenadas"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <CoordenadasPage />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <Configuracao />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/ifood"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <Configuracao />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/pedidos-pendentes"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <PedidosPendentes />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/perfil"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <Perfil />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/pedidos-pendentes"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <PedidosPendentes />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/impressora"
                element={
                  <PrivateRoute>
                    <StoreAuthProvider>
                      <StoreAccessControl>
                        <Impressora />
                      </StoreAccessControl>
                    </StoreAuthProvider>
                  </PrivateRoute>
                }
              />
              <Route path="/termos/:type" element={<Termos />} />
              <Route path="/" element={<Landing />} />
            </Routes>
            </Suspense>
          </GlobalNotificationsProvider>
        </SystemMessageProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

function SuporteApp() {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <SystemMessageProvider>
          <GlobalNotificationsProvider userType="support">
            <Suspense fallback={<PageLoading />}>
            <Routes>
              {/* ✅ Login público - SEM SuporteAuthProvider */}
              <Route path="/login" element={<SuporteLogin />} />
              <Route path="/register" element={<RegisterSupport />} />

              {/* ✅ Rotas protegidas - COM SuporteAuthProvider */}
              <Route
                path="/dashboard"
                element={
                  <SuporteAuthProvider>
                    <PrivateRoute>
                      <SuporteDashboard />
                    </PrivateRoute>
                  </SuporteAuthProvider>
                }
              />
              <Route
                path="/relatorios"
                element={
                  <SuporteAuthProvider>
                    <PrivateRoute>
                      <Relatorios />
                    </PrivateRoute>
                  </SuporteAuthProvider>
                }
              />
              <Route
                path="/ocorrencias"
                element={
                  <SuporteAuthProvider>
                    <PrivateRoute>
                      <Occurrences />
                    </PrivateRoute>
                  </SuporteAuthProvider>
                }
              />
              <Route
                path="/notificacoes"
                element={
                  <SuporteAuthProvider>
                    <PrivateRoute>
                      <SupportNotifications />
                    </PrivateRoute>
                  </SuporteAuthProvider>
                }
              />
              <Route
                path="/chat"
                element={
                  <SuporteAuthProvider>
                    <PrivateRoute>
                      <ChatPage />
                    </PrivateRoute>
                  </SuporteAuthProvider>
                }
              />
              <Route
                path="/conversation"
                element={
                  <SuporteAuthProvider>
                    <PrivateRoute>
                      <ConversationsPage />
                    </PrivateRoute>
                  </SuporteAuthProvider>
                }
              />
              <Route
                path="/mapa"
                element={
                  <SuporteAuthProvider>
                    <PrivateRoute>
                      <SupportMapPage />
                    </PrivateRoute>
                  </SuporteAuthProvider>
                }
              />
              <Route
                path="/pedidos"
                element={
                  <SuporteAuthProvider>
                    <PrivateRoute>
                      <OrdersPage />
                    </PrivateRoute>
                  </SuporteAuthProvider>
                }
              />
              <Route
                path="/motoboys"
                element={
                  <SuporteAuthProvider>
                    <PrivateRoute>
                      <MotoboysPage />
                    </PrivateRoute>
                  </SuporteAuthProvider>
                }
              />
              <Route
                path="/estabelecimentos"
                element={
                  <SuporteAuthProvider>
                    <PrivateRoute>
                      <EstabelecimentosSuporteWrapper />
                    </PrivateRoute>
                  </SuporteAuthProvider>
                }
              />
              <Route
                path="/precificacao"
                element={
                  <SuporteAuthProvider>
                    <PrivateRoute>
                      <PrecificacaoPage />
                    </PrivateRoute>
                  </SuporteAuthProvider>
                }
              />
              <Route
                path="/contratos"
                element={
                  <SuporteAuthProvider>
                    <PrivateRoute>
                      <ContratosPage />
                    </PrivateRoute>
                  </SuporteAuthProvider>
                }
              />
              <Route
                path="/financeiro"
                element={
                  <SuporteAuthProvider>
                    <PrivateRoute>
                      <FinanceiroSuporte />
                    </PrivateRoute>
                  </SuporteAuthProvider>
                }
              />
              <Route
                path="/notificacao-em-massa"
                element={
                  <SuporteAuthProvider>
                    <PrivateRoute>
                      <MassNotificationPage />
                    </PrivateRoute>
                  </SuporteAuthProvider>
                }
              />

              {/* Redirecionamentos */}
              <Route path="/" element={<Navigate to="/login" />} />
              <Route path="*" element={<Navigate to="/login" />} />
            </Routes>
            </Suspense>
          </GlobalNotificationsProvider>
        </SystemMessageProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

function AdminApp() {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <SystemMessageProvider>
          <GlobalNotificationsProvider userType="admin">
            <Suspense fallback={<PageLoading />}>
            <Routes>
              <Route path="/login" element={<LoginAdmin />} />

              <Route
                path="/*"
                element={
                  <AdminAuthProvider>
                    <Routes>
                      <Route path="/" element={<AdminDashboard />} />
                      <Route path="/dashboard" element={<AdminDashboard />} />
                      <Route
                        path="/stores"
                        element={<EstabelecimentosPage />}
                      />
                      <Route path="/pedidos" element={<OrdersPage />} />
                      <Route path="/drivers" element={<MotoboysPage />} />
                      <Route path="/corridas" element={<AdminCorridas />} />
                      <Route path="/contratos" element={<ContratosPage />} />
                      <Route path="/ocorrencias" element={<Occurrences />} />
                      <Route path="/financeiro" element={<AdminFinanceiro />} />
                      <Route path="/settings" element={<PrecificacaoPage />} />
                      <Route path="/mapa" element={<SupportMapPage />} />
                      <Route path="/chat" element={<ChatPage />} />
                      <Route path="/conversation" element={<ConversationsPage />} />
                      <Route
                        path="/notificacao-em-massa"
                        element={<MassNotificationPage />}
                      />
                      <Route path="/fila" element={<Fila />} />
                      <Route
                        path="/relatorios/ocorrencias"
                        element={<OcorrenciasReport />}
                      />
                      <Route
                        path="/relatorios/financeiro"
                        element={<RelatorioFinanceiro />}
                      />
                      <Route
                        path="/relatorios/financeiro-agrupado"
                        element={<RelatorioFinanceiroAgrupado />}
                      />
                      <Route
                        path="/notifications"
                        element={<AdminNotifications />}
                      />
                      <Route path="/tipos" element={<TiposPage />} />
                      <Route path="/metadados" element={<MetadadosPage />} />
                      <Route
                        path="/marketplace/repasse"
                        element={<MarketplaceRepasse />}
                      />
                      <Route path="*" element={<Navigate to="/dashboard" />} />
                      <Route path="/suporte" element={<SupportPage />} />
                    </Routes>
                  </AdminAuthProvider>
                }
              />
            </Routes>
            </Suspense>
          </GlobalNotificationsProvider>
        </SystemMessageProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

function App() {
  const { isAdmin, isSuporte } = useSubdomain();
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        {isAdmin ? <AdminApp /> : isSuporte ? <SuporteApp /> : <CustomerApp />}
      </Router>
    </ThemeProvider>
  );
}

export default App;
